import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  Form,
  Link,
  redirect,
  useActionData,
  useLoaderData,
  useNavigation,
} from 'react-router'
import { AxiosError } from 'axios'
import { useEffect, useRef } from 'react'
import Loader from '~/components/ui/Loader'
import { requestVerificationCode } from '~/lib/auth/api'
import { redirectCookie, verificationCodeCookie } from '~/lib/auth/cookies'
import useTranslator from '~/lib/useTranslator'
import { Button } from '~/components/ui/button'

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const email = url.searchParams.get('email')
  const returnTo = url.searchParams.get('returnTo')

  return { email, returnTo }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const email = formData.get('email')?.toString()
  const returnTo = formData.get('returnTo')?.toString()

  if (!email) {
    return { type: 'error', error: 'Email is required' }
  }

  let ref = ''
  try {
    const { data } = await requestVerificationCode('email', email, true)
    ref = data.ref
  } catch (error) {
    if (error instanceof AxiosError) {
      return { type: 'error', error: error.response?.data.errors[0].message }
    }
    console.error(error)
    return { type: 'error', error: 'Something went wrong' }
  }

  if (ref) {
    const headers = new Headers()
    headers.append('Set-Cookie', await verificationCodeCookie.serialize({ ref }))
    if (returnTo) {
      headers.append('Set-Cookie', await redirectCookie.serialize(returnTo))
    }
    throw redirect('/signin/code', { headers })
  }

  return null
}

export default function SigninIndex() {
  const Translator = useTranslator()
  const { email, returnTo } = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const { state } = useNavigation()
  const emailRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (window?.localStorage.getItem('previousEmail')) {
      emailRef.current!.value = window.localStorage.getItem('previousEmail')!
      emailRef.current!.focus()
    }
  }, [])

  const isSubmitting = state === 'submitting'

  return (
    <Form method="post">
      {returnTo && <input type="hidden" name="returnTo" value={returnTo} />}
      <div className="absolute top-2 left-2">
        <Link to="/" className="text-white font-medium block bg-white/20 rounded-md px-2 py-1">
          &larr; Back to Heja
        </Link>
      </div>
      <div className="bg-white shadow-xs rounded-xl p-8 max-w-lg w-full flex flex-col gap-4">
        <div className="flex flex-col gap-2 items-center">
          <div className="text-xs opacity-50 uppercase">Log in to</div>
          <img src="/images/heja-logo-green.svg" alt="Heja" className="w-24" />
        </div>

        <p className="text-sm text-slate-600">
          We&apos;ll send you a 4-digit code which you enter in the next step.
        </p>
        <p className="flex flex-col">
          <label htmlFor="email">{Translator.singular('Email')}</label>
          <input
            ref={emailRef}
            type="email"
            name="email"
            defaultValue={email ?? ''}
            disabled={isSubmitting}
            onChange={(e) => {
              window?.localStorage?.setItem('previousEmail', e.currentTarget.value)
            }}
          />
          {actionData?.error && <span className="text-red-500 block">{actionData.error}</span>}
        </p>
        <p className="flex items-center justify-end">
          <Button type="submit" size="lg" className='w-full cursor-pointer' disabled={isSubmitting}>
            {isSubmitting && <Loader />} Continue
          </Button>
        </p>
        {/* <hr />
        <div className="flex flex-col items-center gap-4">
          <div className="text-sm">
            Don&apos;t have an account? Download the Heja app and create one.
          </div>
          <DownloadButtons />
        </div> */}
      </div>
    </Form>
  )
}
